import {announce} from '@github-ui/aria-live'
import {parseFileAnchor} from '@github-ui/blob-anchor'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import linkButtonStyles from '@github-ui/code-view-shared/css/LinkButton.module.css'
import {useCurrentRepository} from '@github-ui/current-repository'
import {blamePath, blobPath} from '@github-ui/paths'
import {useNavigate} from '@github-ui/use-navigate'
import {Dialog, LinkButton, TextInput} from '@primer/react'
import {useEffect, useEffectEvent, useRef, useState} from 'react'

import {useCurrentBlob} from '../../hooks/CurrentBlob'
import {useCursorEndLineNumber, useCursorStartLineNumber, useIsCursorEnabled} from '../../hooks/use-cursor-navigation'
import {scrollLineIntoView} from '../../hooks/use-scroll-line-into-view'
import styles from './BlobNavigationButtons.module.css'

export function useWorkflowRedirectUrl() {
  const blobPayload = useCurrentBlob()
  return blobPayload.workflowRedirectUrl
}

export function GoToLineDialog({
  onBlamePage,
  onDismiss,
  maxLineNumber,
}: {
  onBlamePage: boolean
  onDismiss: () => void
  maxLineNumber?: number
}) {
  const navigate = useNavigate()
  const shouldUseCursor = useIsCursorEnabled()
  const startLineNum = useCursorStartLineNumber()
  const endLineNum = useCursorEndLineNumber()
  const [lineNumber, setLineNumber] = useState(shouldUseCursor ? startLineNum : 1)
  const [valid, setValid] = useState(true)
  const [changeDescribeBy, setChangeDescribeBy] = useState(true)
  const [secondLineNumber, setSecondLineNumber] = useState<number | null>(
    startLineNum !== endLineNum && shouldUseCursor ? endLineNum : null,
  )
  const path = usePath()
  const refInfo = useRefInfo()
  const repo = useCurrentRepository()
  const inputBoxRef = useRef<HTMLInputElement>(null)

  const hash = lineNumber ? `#L${lineNumber}${secondLineNumber ? `-L${secondLineNumber}` : ''}` : ''
  const href = onBlamePage
    ? blamePath({
        repo: repo.name,
        owner: repo.ownerLogin,
        filePath: path,
        commitish: refInfo.name,
      })
    : blobPath({
        repo: repo.name,
        owner: repo.ownerLogin,
        filePath: path,
        commitish: refInfo.name,
      })

  const focusInputBoxRefOnLoad = useEffectEvent(() => {
    if (inputBoxRef && inputBoxRef.current) {
      inputBoxRef.current.value = shouldUseCursor
        ? `${startLineNum}${endLineNum !== startLineNum ? `-${endLineNum}` : ''}`
        : '1'

      inputBoxRef.current.focus()
    }
  })
  useEffect(() => {
    focusInputBoxRefOnLoad()
  }, [])

  function handleInputChange(inputValue: string) {
    let isValid = true
    let newLineNumber = lineNumber
    let newSecondLineNumber = secondLineNumber
    if (inputValue.trim() === '') {
      newLineNumber = 1
    }
    if (inputValue.startsWith('-') && maxLineNumber) {
      const intValue = parseInt(inputValue, 10)
      if (!Number.isNaN(intValue) && intValue < 0) {
        let numberToGoTo = maxLineNumber + intValue + 1
        if (numberToGoTo <= 0) {
          numberToGoTo = 1
          isValid = false
        }
        newLineNumber = numberToGoTo
      }
    } else if (inputValue.includes('-')) {
      const [firstLine, secondLine] = inputValue.split('-')
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const firstIntValue = parseInt(firstLine!, 10)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const secondIntvalue = parseInt(secondLine!, 10)
      if (!Number.isNaN(firstIntValue) && firstIntValue > 0) {
        newLineNumber = maxLineNumber ? Math.min(firstIntValue, maxLineNumber) : firstIntValue
        isValid = maxLineNumber !== undefined && firstIntValue <= maxLineNumber
      }
      if (!Number.isNaN(secondIntvalue) && secondIntvalue > 0) {
        newSecondLineNumber = maxLineNumber ? Math.min(secondIntvalue, maxLineNumber) : secondIntvalue
        isValid = isValid && maxLineNumber !== undefined && secondIntvalue <= maxLineNumber
      }
    } else {
      const intValue = parseInt(inputValue, 10)
      if (!Number.isNaN(intValue) && intValue > 0) {
        newLineNumber = maxLineNumber ? Math.min(intValue, maxLineNumber) : intValue
        isValid = maxLineNumber !== undefined && intValue <= maxLineNumber
      } else {
        isValid = inputValue !== '' ? false : true
      }
    }
    setLineNumber(newLineNumber)
    setSecondLineNumber(newSecondLineNumber)
    setValid(isValid)
    if (isValid && !changeDescribeBy) {
      setChangeDescribeBy(true)
    }
  }

  const handleGoToLine = (url: string, newHash: string) => {
    // update our url and trigger any necessary changes on path/hash change (namely highlighting)
    navigate(`${url}${newHash}`)
    // hash changes don't automatically scroll to the line as of commit 252468
    const anchInfo = parseFileAnchor(newHash)
    if (!anchInfo.blobRange?.start?.line || !valid) {
      if (!valid) {
        announce('Invalid line number', {assertive: true})
      }
      setChangeDescribeBy(valid)
      setTimeout(() => {
        inputBoxRef.current?.focus()
      }, 25)
      return
    }
    scrollLineIntoView({line: anchInfo.blobRange.start.line})

    onDismiss()
  }

  //TODO: change the 'Go' linkbutton below back to a button with 'as="a"' once the primer react bug is fixed
  return (
    <Dialog title="Jump to line" onClose={onDismiss} width="large">
      <div className={styles.dialogBody}>
        <div className={styles.inputWrapper}>
          <TextInput
            ref={inputBoxRef}
            aria-label="Line number"
            aria-invalid={!changeDescribeBy}
            aria-describedby={changeDescribeBy ? undefined : 'goToLineErrorValidation'}
            className={styles.lineInput}
            placeholder="Jump to line..."
            onChange={e => {
              const value = e.target.value
              if (inputBoxRef && inputBoxRef.current) {
                inputBoxRef.current.value = value
              }
              handleInputChange(value)
            }}
            onFocus={() => {
              if (inputBoxRef && inputBoxRef.current) {
                inputBoxRef.current.select()
              }
            }}
            onKeyDown={e => {
              // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
              if (e.key !== 'Enter') return
              // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
              if (e.key === 'Enter' && !valid) {
                announce('Invalid line number', {assertive: true})
                setChangeDescribeBy(valid)
                setTimeout(() => {
                  inputBoxRef.current?.focus()
                }, 25)
                return
              }
              handleGoToLine(href, hash)
            }}
          />
        </div>
        <LinkButton
          href={changeDescribeBy ? href + hash : undefined}
          onClick={(e: React.MouseEvent) => {
            // The default click action for a semantic link is to go to the URL specified in `href`, but we need to
            // ensure the custom scroll-to-line behavior executes. As a result, preventing the default event behavior
            // here and handling the navigation ourselves keeps behavior consistent.
            e.preventDefault()
            handleGoToLine(href, hash)
          }}
          className={linkButtonStyles.linkButton}
        >
          Go
        </LinkButton>
      </div>
      {!changeDescribeBy && (
        <div id="goToLineErrorValidation" className={styles.errorAlert}>
          Invalid line number
        </div>
      )}
    </Dialog>
  )
}
